import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { process } from '../mockData';
import { Link } from 'react-router-dom';

export const Process = () => {
  return (
    <div className="process-page pt-20">
      {/* Hero Section */}
      <section className="process-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/10 border border-red-600/20 rounded-full mb-8">
              <span className="text-red-500 text-sm font-medium">Explore → Craft → Create → Scale</span>
            </div>
            <h1 className="text-6xl font-bold text-white mb-6">
              From Insight to Impact:<br />
              <span className="text-gradient">The RADIUS Process</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              A proven 4-phase approach that connects strategic thinking with practical execution, delivering measurable results at every stage.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="process-steps py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {process.map((step, index) => (
              <div key={index} className="process-step-container">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Content */}
                  <div className={`process-content ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="inline-block px-4 py-2 bg-red-600/10 border border-red-600/20 rounded-full mb-6">
                      <span className="text-red-500 font-semibold text-sm">Phase {index + 1}</span>
                    </div>
                    
                    <h2 className="text-5xl font-bold text-white mb-4">
                      {step.phase}
                    </h2>
                    
                    <h3 className="text-2xl font-semibold text-gray-300 mb-6">
                      {step.title}
                    </h3>
                    
                    <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                      {step.description}
                    </p>

                    <div className="bg-black/50 border border-gray-800 rounded-2xl p-6 mb-6">
                      <h4 className="text-white font-semibold mb-4">Key Activities:</h4>
                      <ul className="space-y-3">
                        {step.activities.map((activity, idx) => (
                          <li key={idx} className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-red-500 rounded-full" />
                            <span className="text-gray-300">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-start gap-3 text-sm">
                      <span className="text-gray-500 font-medium">Deliverables:</span>
                      <span className="text-gray-400">{step.deliverables}</span>
                    </div>
                  </div>

                  {/* Visual */}
                  <div className={`process-visual ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <Card className="process-visual-card relative overflow-hidden group">
                      <CardContent className="p-0">
                        <div className="aspect-square bg-gradient-to-br from-gray-800 to-black flex items-center justify-center relative">
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(220,38,38,0.1)_0%,transparent_70%)]" />
                          <div className="text-9xl font-bold text-gradient opacity-20 group-hover:opacity-30 transition-opacity">
                            {index + 1}
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-6xl font-bold text-white mb-2">{step.phase}</div>
                              <div className="text-red-500 font-medium">{step.title}</div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Connector (not for last item) */}
                {index < process.length - 1 && (
                  <div className="flex justify-center my-12">
                    <div className="w-1 h-16 bg-gradient-to-b from-red-600 to-transparent" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Overview */}
      <section className="timeline-section py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              Typical Project Timeline
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              While every project is unique, here's a general timeline for a comprehensive brand and growth engagement.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Card className="timeline-card">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-gradient mb-4">2-3</div>
                <div className="text-gray-400 text-sm mb-2">WEEKS</div>
                <div className="text-white font-semibold mb-2">Explore</div>
                <div className="text-gray-500 text-sm">Research & Discovery</div>
              </CardContent>
            </Card>

            <Card className="timeline-card">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-gradient mb-4">3-4</div>
                <div className="text-gray-400 text-sm mb-2">WEEKS</div>
                <div className="text-white font-semibold mb-2">Craft</div>
                <div className="text-gray-500 text-sm">Strategy Development</div>
              </CardContent>
            </Card>

            <Card className="timeline-card">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-gradient mb-4">6-8</div>
                <div className="text-gray-400 text-sm mb-2">WEEKS</div>
                <div className="text-white font-semibold mb-2">Create</div>
                <div className="text-gray-500 text-sm">Creative Production</div>
              </CardContent>
            </Card>

            <Card className="timeline-card">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-gradient mb-4">Ongoing</div>
                <div className="text-gray-400 text-sm mb-2">CONTINUOUS</div>
                <div className="text-white font-semibold mb-2">Scale</div>
                <div className="text-gray-500 text-sm">Launch & Optimize</div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-500 text-sm">
              * Timeline varies based on project scope and complexity
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-br from-red-600/5 to-transparent">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Let's Map Your Journey
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Every project is unique. Let's discuss how our process can be tailored to your specific goals and timeline.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary group">
              Start the Conversation
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Process;
